import { useState } from 'react';
import { getPlaceholderUrl } from '../lib/imageProxy';

const wrapperStyle = { position: 'relative', overflow: 'hidden', width: '100%', height: '100%' };

const placeholderBaseStyle = {
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  width: '100%',
  height: '100%',
  filter: 'blur(20px)',
  transform: 'scale(1.1)',
  transition: 'opacity 0.5s ease',
};

const realImgBaseStyle = {
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  width: '100%',
  height: '100%',
  transition: 'opacity 0.5s ease, transform 0.6s ease',
};

// Drop-in <img> replacement that shows a tiny blurred thumbnail while the full image loads,
// then fades it out as the real image fades in. Falls back to a plain <img> for non-proxied URLs.
const BlurImage = ({ src, alt, objectFit = 'cover', loading = 'lazy', onError, onLoad, style, ...rest }) => {
  const [loaded, setLoaded] = useState(false);
  const placeholderSrc = getPlaceholderUrl(src);

  if (!placeholderSrc) {
    // No blur-up wrapper here, so nothing else constrains this <img> to its
    // container — without explicit sizing it renders at its intrinsic
    // dimensions and can blow out a fixed-size frame (e.g. the custom show
    // history card) instead of filling/cropping it like the proxied path
    // below does. Match that path's sizing so both branches fit the same.
    return (
      <img
        src={src}
        alt={alt}
        loading={loading}
        onError={onError}
        onLoad={onLoad}
        style={{ width: '100%', height: '100%', objectFit, ...style }}
        {...rest}
      />
    );
  }

  const handleLoad = (e) => {
    setLoaded(true);
    onLoad?.(e);
  };

  return (
    <div style={wrapperStyle}>
      <img
        src={placeholderSrc}
        alt=""
        aria-hidden="true"
        style={{ ...placeholderBaseStyle, objectFit, opacity: loaded ? 0 : 1 }}
      />
      <img
        src={src}
        alt={alt}
        loading={loading}
        onError={onError}
        onLoad={handleLoad}
        style={{ ...realImgBaseStyle, objectFit, opacity: loaded ? 1 : 0 }}
        {...rest}
      />
    </div>
  );
};

export default BlurImage;
